import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import { prepareImageForUpload } from '../utils/compressImage';

const MOTIVO_MAX = 100;

/** Alineado con Multer en sanciones-registrar (5 MB). */
const SANCION_FOTO_MAX_BYTES = 5 * 1024 * 1024;

const toYmdLocal = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatFechaEs = (ymd) => {
  const [y, m, d] = String(ymd).split('-');
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
};

const normalizeSearch = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const Sanciones = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const fechaHoy = useMemo(() => toYmdLocal(), []);

  const [destino, setDestino] = useState(''); // V | S
  const [personalLista, setPersonalLista] = useState([]);
  const [loadingPersonal, setLoadingPersonal] = useState(false);
  const [personaSearch, setPersonaSearch] = useState('');
  const [personaSeleccionada, setPersonaSeleccionada] = useState(null);
  const [showPersonaResults, setShowPersonaResults] = useState(false);

  const [catalogo, setCatalogo] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);

  const [tipoId, setTipoId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [modalConfirm, setModalConfirm] = useState(null);
  const [modalExito, setModalExito] = useState(null);

  const [puedeSupervisor, setPuedeSupervisor] = useState(false);
  const [configLista, setConfigLista] = useState(false);

  const capturaInputId = useMemo(() => `sancion-foto-${Date.now()}`, []);
  const personaHabilitada = Boolean(personaSeleccionada);

  useEffect(() => {
    if (user && user.rol !== 'S') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelado = false;
    const cargarConfig = async () => {
      if (!user?.username) return;
      try {
        const url = new URL(getApiUrl('SUPERVISION', 'SANCIONES_CONFIG'));
        url.searchParams.set('username', user.username);
        const res = await fetch(url.toString(), { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (cancelado) return;
        const puede = Boolean(res.ok && data.success && data.data?.puedeSancionarSupervisores);
        setPuedeSupervisor(puede);
        if (!puede) setDestino('V');
      } catch {
        if (!cancelado) {
          setPuedeSupervisor(false);
          setDestino('V');
        }
      } finally {
        if (!cancelado) setConfigLista(true);
      }
    };
    cargarConfig();
    return () => {
      cancelado = true;
    };
  }, [user?.username]);

  useEffect(() => {
    if (!fotoPreview) return undefined;
    return () => URL.revokeObjectURL(fotoPreview);
  }, [fotoPreview]);

  const tipoSeleccionado = useMemo(
    () => catalogo.find((m) => String(m.id) === String(tipoId)) || null,
    [catalogo, tipoId]
  );

  const resultadosPersona = useMemo(() => {
    if (personaSeleccionada) return [];
    const q = normalizeSearch(personaSearch);
    if (!q) return [];
    return personalLista
      .filter((p) => {
        const nombre = normalizeSearch(p.nombre);
        const id = normalizeSearch(p.id);
        return nombre.includes(q) || id.includes(q);
      })
      .slice(0, 40);
  }, [personalLista, personaSearch, personaSeleccionada]);

  const cargarPersonal = useCallback(async () => {
    if (!destino || !user?.username) {
      setPersonalLista([]);
      return;
    }
    setLoadingPersonal(true);
    setError('');
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'SANCIONES_PERSONAL_LISTA'));
      url.searchParams.set('username', user.username);
      url.searchParams.set('destino', destino);
      const res = await fetch(url.toString(), { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'No se pudo cargar el personal.');
        setPersonalLista([]);
        return;
      }
      setPersonalLista(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setError(e.message || 'Error al cargar personal.');
      setPersonalLista([]);
    } finally {
      setLoadingPersonal(false);
    }
  }, [destino, user?.username]);

  const cargarCatalogo = useCallback(async () => {
    if (!destino || !user?.username) {
      setCatalogo([]);
      return;
    }
    setLoadingCatalogo(true);
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'CATALOGO_MULTAS'));
      url.searchParams.set('username', user.username);
      url.searchParams.set('destino', destino);
      const res = await fetch(url.toString(), { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setCatalogo([]);
        setError(data.error || 'No se pudo cargar el catálogo de multas.');
        return;
      }
      setCatalogo(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setCatalogo([]);
      setError(e.message || 'Error al cargar el catálogo de multas.');
    } finally {
      setLoadingCatalogo(false);
    }
  }, [destino, user?.username]);

  const limpiarDetalleSancion = useCallback(() => {
    setTipoId('');
    setMotivo('');
    setFotoArchivo(null);
    setFotoPreview((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return '';
    });
    const input = document.getElementById(capturaInputId);
    if (input) input.value = '';
  }, [capturaInputId]);

  useEffect(() => {
    setPersonaSeleccionada(null);
    setPersonaSearch('');
    setShowPersonaResults(false);
    setPersonalLista([]);
    setCatalogo([]);
    limpiarDetalleSancion();
    if (destino) {
      cargarPersonal();
      cargarCatalogo();
    }
  }, [destino, cargarPersonal, cargarCatalogo, limpiarDetalleSancion]);

  const handleDestinoChange = (value) => {
    setDestino(value);
    setError('');
  };

  const seleccionarPersona = (p) => {
    setPersonaSeleccionada(p);
    setPersonaSearch(p.nombre || '');
    setShowPersonaResults(false);
    setError('');
    setTipoId('');
    setMotivo('');
  };

  const limpiarPersona = () => {
    setPersonaSeleccionada(null);
    setPersonaSearch('');
    setShowPersonaResults(false);
    limpiarDetalleSancion();
  };

  const handleFotoChange = async (e) => {
    const input = e.target;
    const file = input.files?.[0] || null;
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    if (!file) {
      setFotoArchivo(null);
      setFotoPreview('');
      return;
    }
    const prepared = await prepareImageForUpload(file, {
      maxBytes: SANCION_FOTO_MAX_BYTES,
      label: 'foto de evidencia'
    });
    if (!prepared.ok) {
      setFotoArchivo(null);
      setFotoPreview('');
      setError(prepared.error);
      input.value = '';
      return;
    }
    setError('');
    setFotoArchivo(prepared.file);
    setFotoPreview(URL.createObjectURL(prepared.file));
  };

  const limpiarFoto = () => {
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFotoArchivo(null);
    setFotoPreview('');
    const input = document.getElementById(capturaInputId);
    if (input) input.value = '';
  };

  const reiniciarFormulario = () => {
    setDestino('');
    setPersonaSeleccionada(null);
    setPersonaSearch('');
    setShowPersonaResults(false);
    setPersonalLista([]);
    setCatalogo([]);
    setTipoId('');
    setMotivo('');
    limpiarFoto();
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!destino) {
      setError('Seleccione Supervisor o Vigilante.');
      return;
    }
    if (!personaSeleccionada) {
      setError('Seleccione una persona de la lista.');
      return;
    }
    if (!tipoId || !tipoSeleccionado) {
      setError('Seleccione el tipo de multa.');
      return;
    }
    const motivoTrim = motivo.trim();
    if (!motivoTrim) {
      setError('Ingrese el motivo de la sanción.');
      return;
    }
    if (motivoTrim.length > MOTIVO_MAX) {
      setError(`El motivo no puede superar ${MOTIVO_MAX} caracteres.`);
      return;
    }
    if (!fotoArchivo) {
      setError('Debe adjuntar una foto de evidencia.');
      return;
    }

    // Resolver el lugar solo para mostrarlo en la confirmación (no inserta nada).
    setPreparing(true);
    let lugar = 'SANCIÓN BITÁCORA';
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'SANCIONES_LUGAR_PREVIEW'));
      url.searchParams.set('username', user?.username || '');
      url.searchParams.set('destino', destino);
      url.searchParams.set('personaId', String(personaSeleccionada.id));
      const res = await fetch(url.toString(), { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.data?.lugar) {
        lugar = data.data.lugar;
      }
    } catch {
      // Si falla la previsualización se muestra el respaldo; el registro real recalcula el lugar.
    } finally {
      setPreparing(false);
    }

    setModalConfirm({
      destino: destino === 'V' ? 'Vigilante' : 'Supervisor',
      persona: personaSeleccionada.nombre,
      lugar,
      fecha: fechaHoy,
      tipo: tipoSeleccionado.tipo,
      motivo: motivoTrim
    });
  };

  const confirmarRegistro = async () => {
    if (!modalConfirm || submitting) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('username', user?.username || '');
      formData.append('destino', destino);
      formData.append('personaId', String(personaSeleccionada.id));
      formData.append('catalogoId', String(tipoSeleccionado.id));
      formData.append('motivo', motivo.trim());
      formData.append('fotoEvidencia', fotoArchivo);

      const res = await fetch(getApiUrl('SUPERVISION', 'SANCIONES_REGISTRAR'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setModalConfirm(null);
        if (res.status === 413) {
          setError(
            data.error
              || 'No se pudo registrar la sanción: la imagen es demasiado grande (413). Intente de nuevo con otra foto.'
          );
        } else {
          setError(data.error || `No se pudo registrar la sanción (${res.status}).`);
        }
        return;
      }

      setModalConfirm(null);
      setModalExito({ generoFalta: Boolean(data.data?.genero_falta) });
      reiniciarFormulario();
    } catch (err) {
      setModalConfirm(null);
      setError(err.message || 'Error de conexión al registrar la sanción.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.rol !== 'S') {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 sm:p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sanciones</h1>
           
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {puedeSupervisor && (
              <div>
                <p className="block text-sm font-medium text-gray-800 mb-2">
                  Personal <span className="text-red-600">*</span>
                </p>
                <div className="flex items-center gap-6">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sancion-destino"
                      value="S"
                      checked={destino === 'S'}
                      onChange={() => handleDestinoChange('S')}
                      disabled={submitting}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-800">Supervisor</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sancion-destino"
                      value="V"
                      checked={destino === 'V'}
                      onChange={() => handleDestinoChange('V')}
                      disabled={submitting}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-800">Vigilante</span>
                  </label>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="sancion-persona-search" className="block text-sm font-medium text-gray-800 mb-1">
                Buscar persona <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  id="sancion-persona-search"
                  type="text"
                  value={personaSeleccionada ? personaSeleccionada.nombre : personaSearch}
                  onChange={(e) => {
                    setPersonaSeleccionada(null);
                    setPersonaSearch(e.target.value);
                    setShowPersonaResults(true);
                    limpiarDetalleSancion();
                  }}
                  onFocus={() => {
                    if (!personaSeleccionada && personaSearch.trim()) setShowPersonaResults(true);
                  }}
                  disabled={submitting || !configLista || !destino || loadingPersonal}
                  placeholder={
                    !configLista
                      ? 'Cargando…'
                      : !destino
                        ? 'Seleccione Supervisor o Vigilante…'
                        : loadingPersonal
                          ? 'Cargando lista…'
                          : 'Buscar por apellido, nombre o cédula…'
                  }
                  autoComplete="off"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
                {personaSeleccionada && (
                  <button
                    type="button"
                    onClick={limpiarPersona}
                    disabled={submitting}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-red-600 px-1"
                  >
                    Cambiar
                  </button>
                )}
              </div>
              {showPersonaResults && !personaSeleccionada && destino && personaSearch.trim().length > 0 && (
                <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm max-h-64 overflow-y-auto">
                  {loadingPersonal ? (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">Cargando…</div>
                  ) : resultadosPersona.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">Sin coincidencias</div>
                  ) : (
                    resultadosPersona.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => seleccionarPersona(p)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{p.nombre || '—'}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
              {loadingPersonal && <p className="text-xs text-gray-500 mt-1">Cargando lista…</p>}
            </div>

            {personaHabilitada && (
            <div>
              <div>
                <label htmlFor="sancion-fecha" className="block text-sm font-medium text-gray-800 mb-1">
                  Fecha <span className="text-red-600">*</span>
                </label>
                <input
                  id="sancion-fecha"
                  type="text"
                  readOnly
                  disabled={!personaHabilitada}
                  value={formatFechaEs(fechaHoy)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-800 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Solo se pueden registrar sanciones del día actual.</p>
              </div>

              <div className="mt-5">
                <label htmlFor="sancion-tipo" className="block text-sm font-medium text-gray-800 mb-1">
                  Tipo de multa <span className="text-red-600">*</span>
                </label>
                <select
                  id="sancion-tipo"
                  value={tipoId}
                  onChange={(e) => setTipoId(e.target.value)}
                  disabled={submitting || !personaHabilitada || loadingCatalogo}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {!personaHabilitada
                      ? 'Seleccione primero una persona…'
                      : loadingCatalogo
                        ? 'Cargando catálogo…'
                        : 'Seleccione…'}
                  </option>
                  {catalogo.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.tipo}
                    </option>
                  ))}
                </select>
                {personaHabilitada && !loadingCatalogo && catalogo.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    No hay tipos de multa para este destino en el catálogo.
                  </p>
                )}
              </div>

              <div className="mt-5">
                <label htmlFor="sancion-motivo" className="block text-sm font-medium text-gray-800 mb-1">
                  Motivo <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="sancion-motivo"
                  rows={3}
                  maxLength={MOTIVO_MAX}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value.slice(0, MOTIVO_MAX))}
                  disabled={submitting || !personaHabilitada}
                  placeholder="Detalle breve de la sanción…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {motivo.length}/{MOTIVO_MAX}
                </p>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Foto de evidencia <span className="text-red-600">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => document.getElementById(capturaInputId)?.click()}
                  disabled={submitting || !personaHabilitada}
                  className="w-full px-4 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Capturar / seleccionar foto
                </button>
                <input
                  id={capturaInputId}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFotoChange}
                  disabled={submitting || !personaHabilitada}
                  className="hidden"
                />
                {fotoPreview && (
                  <div className="mt-3">
                    <img
                      src={fotoPreview}
                      alt="Evidencia"
                      className="w-full max-w-xs rounded-lg border border-gray-200 mx-auto"
                    />
                    <button
                      type="button"
                      onClick={limpiarFoto}
                      disabled={!personaHabilitada}
                      className="mt-2 text-xs text-red-600 hover:text-red-700 w-full text-center"
                    >
                      Eliminar foto
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={reiniciarFormulario}
                disabled={submitting || preparing}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-800 font-medium text-sm hover:bg-gray-200 disabled:opacity-50"
              >
                Limpiar
              </button>
              <button
                type="submit"
                disabled={submitting || preparing || !personaHabilitada}
                className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
              >
                {preparing ? 'Cargando…' : 'Registrar sanción'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {modalConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 bg-indigo-600 text-white">
              <h2 className="text-lg font-semibold">Confirmar sanción</h2>
              <p className="text-sm text-indigo-100 mt-0.5">Revise los datos antes de registrar.</p>
            </div>
            <div className="px-6 py-5 space-y-2 text-sm text-gray-800">
              <p>
                <span className="text-gray-500">Destino:</span> {modalConfirm.destino}
              </p>
              <p>
                <span className="text-gray-500">Personal:</span> {modalConfirm.persona}
              </p>
              {modalConfirm.lugar && (
                <p>
                  <span className="text-gray-500">Lugar:</span> {modalConfirm.lugar}
                </p>
              )}
              <p>
                <span className="text-gray-500">Fecha:</span> {formatFechaEs(modalConfirm.fecha)}
              </p>
              <p>
                <span className="text-gray-500">Tipo:</span> {modalConfirm.tipo}
              </p>
              <p>
                <span className="text-gray-500">Motivo:</span> {modalConfirm.motivo}
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                type="button"
                onClick={() => setModalConfirm(null)}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-800 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarRegistro}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? 'Registrando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalExito && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden">
            <div className="px-6 py-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Sanción registrada</h2>
              <p className="text-sm text-gray-600">La sanción se registró correctamente.</p>
              {modalExito.generoFalta && (
                <p className="text-sm text-gray-600 mt-1">
                  También se registró una falta ya que no debe quedrase en el turno.
                </p>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setModalExito(null)}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden">
            <div className="px-6 py-5 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-900 mb-1">No se pudo continuar</h2>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setError('')}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Sanciones;
